import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Radio,
  Button,
  Tag,
  Alert,
  Modal,
  Spin,
  Space,
  Divider,
  message
} from "antd";
import {
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LockOutlined,
  SendOutlined,
  SafetyCertificateOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { isAuthenticated } from "../../../commons/Auth/auth";

const EmailSettings = () => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [settings, setSettings] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState("SYSTEM_DEFAULT");

  const authUser = isAuthenticated();
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/api\/?$/, "");

  const getHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  });

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${apiBase}/api/settings/email-provider`,
        getHeaders()
      );
      if (res.data?.settings) {
        const s = res.data.settings;
        setSettings(s);
        setSelectedProvider(s.providerType || "SYSTEM_DEFAULT");
        form.setFieldsValue({
          providerType: s.providerType || "SYSTEM_DEFAULT",
          fromName: s.fromName || "",
          fromEmail: s.fromEmail || "",
          replyTo: s.replyTo || "",
          smtpHost: s.smtp?.host || "",
          smtpPort: s.smtp?.port || 587,
          smtpSecure: Boolean(s.smtp?.secure),
          smtpUser: s.smtp?.auth?.user || "",
          smtpPass: ""
        });
      }
    } catch (err) {
      message.error(err.response?.data?.errorMessage || "Fehler beim Laden der E-Mail-Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    if (authUser?.email) {
      setTestRecipient(authUser.email);
    }
  }, []);

  const handleSave = async values => {
    try {
      setSaving(true);
      const payload = {
        providerType: values.providerType,
        fromName: values.fromName,
        fromEmail: values.fromEmail,
        replyTo: values.replyTo,
        smtp: {
          host: values.smtpHost,
          port: values.smtpPort,
          secure: values.smtpSecure,
          auth: {
            user: values.smtpUser,
            pass: values.smtpPass || undefined
          }
        }
      };

      const res = await axios.put(
        `${apiBase}/api/settings/email-provider`,
        payload,
        getHeaders()
      );

      message.success(res.data?.message || "E-Mail-Einstellungen erfolgreich gespeichert");
      if (res.data?.settings) {
        setSettings(res.data.settings);
      }
    } catch (err) {
      message.error(err.response?.data?.errorMessage || "Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testRecipient || !testRecipient.includes("@")) {
      message.warning("Bitte geben Sie eine gültige Empfänger-E-Mail-Adresse an");
      return;
    }

    try {
      setTesting(true);
      const res = await axios.post(
        `${apiBase}/api/settings/email-provider/test`,
        { testRecipient },
        getHeaders()
      );

      message.success(res.data?.message || "Test-E-Mail erfolgreich gesendet");
      setTestModalOpen(false);
      fetchSettings();
    } catch (err) {
      message.error(err.response?.data?.errorMessage || "Fehler beim Senden der Test-E-Mail");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "80px 20px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <MailOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
            <span>{t("Email Configuration") || "E-Mail-Konfiguration"}</span>
          </div>
        }
        extra={
          <Button
            type="default"
            icon={<SendOutlined />}
            onClick={() => setTestModalOpen(true)}
          >
            {t("Send Test Email") || "Test-E-Mail senden"}
          </Button>
        }
      >
        <Alert
          message={t("Outbound Email Providers") || "Ausgehende E-Mail-Dienste"}
          description={
            t("Configure how transactional and platform emails are sent for this tenant. Choose between the LeistFusion platform default or your dedicated company SMTP server.") ||
            "Legen Sie fest, wie Plattform- und System-E-Mails für diesen Mandanten versendet werden. Wählen Sie zwischen dem Standard-Systemdienst oder Ihrem eigenen SMTP-Server."
          }
          type="info"
          showIcon
          style={{ marginBottom: "24px" }}
        />

        {settings?.lastTestedAt && (
          <div style={{ marginBottom: "20px", padding: "12px 16px", backgroundColor: "#fafafa", borderRadius: "6px", border: "1px solid #f0f0f0" }}>
            <Space orientation="horizontal" size="middle" wrap>
              <span><strong>Status:</strong></span>
              {settings.lastTestStatus === "SUCCESS" ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t("Verified & Operational") || "Verifiziert & Betriebsbereit"}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="error">
                  {t("Test Failed") || "Test fehlgeschlagen"}
                </Tag>
              )}
              <span><strong>Letzter Test:</strong> {new Date(settings.lastTestedAt).toLocaleString()}</span>
              {settings.lastTestError && (
                <span style={{ color: "#ff4d4f" }}>({settings.lastTestError})</span>
              )}
            </Space>
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={{
            providerType: selectedProvider,
            smtpPort: 587,
            smtpSecure: false
          }}
        >
          <Form.Item
            name="providerType"
            label={<strong>{t("Email Provider Type") || "E-Mail-Dienstanbieter"}</strong>}
          >
            <Radio.Group
              onChange={e => setSelectedProvider(e.target.value)}
              value={selectedProvider}
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <Radio value="SYSTEM_DEFAULT">
                <div>
                  <strong>{t("System Default") || "System-Standard (LeistFusion)"}</strong>
                  <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                    {t("Uses the global server email transport. No additional credentials required.") ||
                      "Verwendet den vorkonfigurierten Systemdienst. Keine zusätzlichen Zugangsdaten erforderlich."}
                  </div>
                </div>
              </Radio>

              <Radio value="CUSTOM_SMTP">
                <div>
                  <strong>{t("Custom SMTP Server") || "Eigener SMTP-Server (Mandantenspezifisch)"}</strong>
                  <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                    {t("Send emails through your own company mail server with authenticated AES-256 encryption.") ||
                      "E-Mails über Ihren firmeneigenen Mailserver versenden. Zugangsdaten werden mit AES-256-GCM geschützt."}
                  </div>
                </div>
              </Radio>

              <Radio value="GOOGLE_WORKSPACE" disabled>
                <div>
                  <Space orientation="horizontal" size="small">
                    <strong>Google Workspace / Gmail</strong>
                    <Tag color="default">Demnächst / Coming soon</Tag>
                  </Space>
                  <div style={{ color: "#bfbfbf", fontSize: "12px" }}>
                    OAuth 2.0 Integration für Google Workspace
                  </div>
                </div>
              </Radio>

              <Radio value="MICROSOFT_365" disabled>
                <div>
                  <Space orientation="horizontal" size="small">
                    <strong>Microsoft 365 / Outlook</strong>
                    <Tag color="default">Demnächst / Coming soon</Tag>
                  </Space>
                  <div style={{ color: "#bfbfbf", fontSize: "12px" }}>
                    Azure AD OAuth Integration für Microsoft 365
                  </div>
                </div>
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Divider />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <Form.Item
              name="fromName"
              label={t("Sender Name") || "Absendername"}
            >
              <Input placeholder="z. B. LeistFusion Kundenservice" />
            </Form.Item>

            <Form.Item
              name="fromEmail"
              label={t("Sender Email") || "Absender-E-Mail"}
            >
              <Input placeholder="noreply@ihre-firma.de" type="email" />
            </Form.Item>
          </div>

          <Form.Item
            name="replyTo"
            label={t("Reply-To Email") || "Antwortadresse (Reply-To)"}
          >
            <Input placeholder="support@ihre-firma.de" type="email" />
          </Form.Item>

          {selectedProvider === "CUSTOM_SMTP" && (
            <>
              <Divider orientation="left">
                <Space orientation="horizontal" size="small">
                  <SafetyCertificateOutlined style={{ color: "#52c41a" }} />
                  <span>{t("SMTP Connection Details") || "SMTP-Verbindungsdaten"}</span>
                </Space>
              </Divider>

              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px" }}>
                <Form.Item
                  name="smtpHost"
                  label={t("SMTP Host") || "SMTP-Server (Host)"}
                  rules={[{ required: selectedProvider === "CUSTOM_SMTP", message: "Bitte Host angeben" }]}
                >
                  <Input placeholder="smtp.ihre-domain.de" />
                </Form.Item>

                <Form.Item
                  name="smtpPort"
                  label={t("SMTP Port") || "Port"}
                  rules={[{ required: selectedProvider === "CUSTOM_SMTP", message: "Bitte Port angeben" }]}
                >
                  <InputNumber style={{ width: "100%" }} min={1} max={65535} />
                </Form.Item>
              </div>

              <Form.Item
                name="smtpSecure"
                label={t("SSL / TLS Secure") || "Sichere Verbindung (SSL / TLS)"}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <Form.Item
                  name="smtpUser"
                  label={t("SMTP Username") || "Benutzername"}
                  rules={[{ required: selectedProvider === "CUSTOM_SMTP", message: "Bitte Benutzername angeben" }]}
                >
                  <Input placeholder="benutzer@ihre-domain.de" />
                </Form.Item>

                <Form.Item
                  name="smtpPass"
                  label={
                    <Space orientation="horizontal" size="small">
                      <span>{t("SMTP Password") || "Passwort"}</span>
                      {settings?.smtp?.auth?.hasPassword && (
                        <Tag color="blue" icon={<LockOutlined />}>
                          {t("Stored securely") || "Verschlüsselt gespeichert"}
                        </Tag>
                      )}
                    </Space>
                  }
                  extra={
                    settings?.smtp?.auth?.hasPassword
                      ? (t("Leave empty to keep the currently stored password") || "Leer lassen, um das bestehende Passwort beizubehalten")
                      : undefined
                  }
                >
                  <Input.Password placeholder="••••••••••••" />
                </Form.Item>
              </div>
            </>
          )}

          <Form.Item style={{ marginTop: "24px" }}>
            <Button type="primary" htmlType="submit" loading={saving} size="large">
              {t("Save Settings") || "Einstellungen speichern"}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Test Email Modal */}
      <Modal
        title={t("Send Test Email") || "Test-E-Mail senden"}
        open={testModalOpen}
        onCancel={() => setTestModalOpen(false)}
        onOk={handleSendTestEmail}
        confirmLoading={testing}
        okText={t("Send") || "Senden"}
        cancelText={t("Cancel") || "Abbrechen"}
      >
        <p>
          {t("Enter an email address to send a verification test email with the currently saved provider settings:") ||
            "Geben Sie eine E-Mail-Adresse ein, um eine Verifizierungs-Test-E-Mail mit den aktuellen Einstellungen zu senden:"}
        </p>
        <Input
          placeholder="empfaenger@beispiel.de"
          type="email"
          value={testRecipient}
          onChange={e => setTestRecipient(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default EmailSettings;
